import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { quietPeriodApi } from "../services/api";
import {
  QuietPeriod,
  QuietPeriodCreate,
  QuietPeriodFilters,
  QuietPeriodUpdate,
} from "../types/quiet_period";

export const useQuietPeriods = (filters: QuietPeriodFilters = {}) => {
  return useQuery({
    queryKey: ["quiet_periods", filters],
    queryFn: () => quietPeriodApi.getQuietPeriods(filters),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useQuietPeriod = (id: number) => {
  return useQuery({
    queryKey: ["quiet_period", id],
    queryFn: () => quietPeriodApi.getQuietPeriod(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: QuietPeriodCreate) =>
      quietPeriodApi.createQuietPeriod(data),

    onSuccess: (created: QuietPeriod) => {
      queryClient.invalidateQueries({
        queryKey: ["quiet_periods"],
      });
      toast.success(`Dynamic date "${created.name}" created successfully`);
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to create dynamic date";
      toast.error(message);
    },
  });
};

export const useUpdateQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: QuietPeriodUpdate }) =>
      quietPeriodApi.updateQuietPeriod(id, data),

    onSuccess: (updated: QuietPeriod) => {
      queryClient.setQueryData(["quiet_period", updated.id], updated);
      queryClient.invalidateQueries({
        queryKey: ["quiet_periods"],
      });
      toast.success(`Dynamic date "${updated.name}" updated successfully`);
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to update dynamic date";
      toast.error(message);
    },
  });
};

export const useDeleteQuietPeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => quietPeriodApi.deleteQuietPeriod(id),

    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: ["quiet_period", deletedId],
      });
      queryClient.invalidateQueries({
        queryKey: ["quiet_periods"],
      });
      toast.success("Dynamic date deleted successfully");
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete dynamic date";
      toast.error(message);
    },
  });
};
